import { useOpenSubscriptionManagement as useOpenSubscriptionManagementShared } from '@griply/hooks';
import { ActionEvents } from '@griply/types';
import { useCallback } from 'react';
import {
  STRIPE_MANAGEMENT_URL_TTL_MS,
  useStripeSubscriptionManagementUrlQuery,
} from 'shared/hooks/queries/use-stripe-subscription-management-url-query';
import { useExpiringValue } from 'shared/hooks/use-expiring-value';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { ID } from 'shared/types/id';

export const useOpenSubscriptionManagement = (
  stripeCustomerId: ID | undefined,
) => {
  const track = useTrackEvents();
  const { data, dataUpdatedAt, isFetching, refetch } =
    useStripeSubscriptionManagementUrlQuery(stripeCustomerId);

  const freshUrl = useExpiringValue(
    data,
    dataUpdatedAt,
    STRIPE_MANAGEMENT_URL_TTL_MS,
  );

  // Apple subscriptions without a management url and Play subscriptions have no Stripe customer to open a portal for.
  const refetchUrl = useCallback(async () => {
    if (!stripeCustomerId) {
      return undefined;
    }
    const result = await refetch();
    return result.data ?? undefined;
  }, [refetch, stripeCustomerId]);

  const onTrigger = useCallback(
    () => track(ActionEvents.CancelSubscription, undefined),
    [track],
  );

  return useOpenSubscriptionManagementShared({
    data: freshUrl ?? undefined,
    isFetching,
    refetch: refetchUrl,
    onTrigger,
  });
};
